/* eslint-disable react-refresh/only-export-components */
/**
 * Loads tenant bug-report config, installs capture hooks (console, auto reporter),
 * and provides global modal state for Bugsnap so active recording survives navigation.
 */

import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { bugReportsApi } from '../../services/api/bugReports'
import { logger } from '../../utils/logger'
import {
  applyPublicConfig,
  resetBugReportPublicConfig,
  subscribeBugReportPublicConfig,
} from './bugReportPublicConfig'
import { BugReportModal } from './BugReportModal'
import { useBugReportEnabled } from './useBugReportEnabled'

export interface BugReportModalContextValue {
  isOpen: boolean
  openModal: () => void
  closeModal: () => void
  voiceDraftingEnabled: boolean
}

export const BugReportModalContext = createContext<BugReportModalContextValue>({
  isOpen: false,
  openModal: () => {},
  closeModal: () => {},
  voiceDraftingEnabled: false,
})

export function useBugReportModal(): BugReportModalContextValue {
  return useContext(BugReportModalContext)
}

export function BugReportCaptureProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth()
  const { enabled, voiceDraftingEnabled } = useBugReportEnabled()
  const [isOpen, setIsOpen] = useState(false)

  const openModal = useCallback(() => setIsOpen(true), [])
  const closeModal = useCallback(() => setIsOpen(false), [])

  useEffect(() => {
    if (!currentUser) {
      resetBugReportPublicConfig()
      return
    }

    let cancelled = false

    const reload = () => {
      void (async () => {
        try {
          const cfg = await bugReportsApi.getConfig()
          if (cancelled) return
          applyPublicConfig(cfg)
        } catch (e) {
          logger.debug('Bug report capture config unavailable', e)
          if (!cancelled) {
            resetBugReportPublicConfig()
          }
        }
      })()
    }

    reload()
    const unsubscribe = subscribeBugReportPublicConfig(reload)

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [currentUser])

  const effectiveIsOpen = Boolean(currentUser && isOpen)

  const contextValue = useMemo(
    () => ({
      isOpen: effectiveIsOpen,
      openModal,
      closeModal,
      voiceDraftingEnabled,
    }),
    [effectiveIsOpen, openModal, closeModal, voiceDraftingEnabled],
  )

  return (
    <BugReportModalContext.Provider value={contextValue}>
      {children}
      {enabled && effectiveIsOpen && (
        <BugReportModal
          isOpen={effectiveIsOpen}
          voiceDraftingEnabled={voiceDraftingEnabled}
          onClose={closeModal}
        />
      )}
    </BugReportModalContext.Provider>
  )
}

